import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, Typography, Skeleton, Button } from '@mui/material';
import { SectionCard, StatusChip } from '../../../components/ui';
import { brandHome } from '../brandHomeTokens';
import { severityToTone } from '../severityTones';
import type { AttentionItem } from '../deriveAttentionItems';

interface AttentionQueueCardProps {
  items: AttentionItem[];
  truncatedCount: number;
  loading?: boolean;
}

export const AttentionQueueCard: React.FC<AttentionQueueCardProps> = ({ items, truncatedCount, loading }) => {
  // The count is every item, including the ones cut off below the fold —
  // `items` is already capped, so its length alone undercounts.
  const totalCount = items.length + truncatedCount;
  return (
    <SectionCard
      dense
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>Needs your attention</Typography>
          <StatusChip label={totalCount} tone="problem" />
        </Box>
      }
      actions={
        <Typography sx={{ fontSize: '12.5px', color: brandHome.color.muted }}>
          Everything here is about your brand at a partner store or in your own feed
        </Typography>
      }
    >
      {loading ? (
        <Box sx={{ p: '13px 20px' }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={44} sx={{ mb: 1, borderRadius: 1 }} />
          ))}
        </Box>
      ) : items.length === 0 ? (
        <Box sx={{ p: '24px 20px', textAlign: 'center' }}>
          <Typography sx={{ fontSize: '13px', color: brandHome.color.muted }}>Nothing needs your attention right now.</Typography>
        </Box>
      ) : (
        <>
          {items.map((item) => (
            <Box
              key={item.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                p: '13px 20px',
                borderBottom: `1px solid ${brandHome.color.rowHairline}`,
              }}
            >
              <StatusChip
                label={item.severity}
                tone={severityToTone(item.severity)}
                dot
                sx={{ flex: '0 0 auto' }}
              />
              <Box sx={{ flex: '1 1 auto', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>{item.title}</Typography>
                <Typography sx={{ fontSize: '12.5px', color: brandHome.color.muted }}>{item.meta}</Typography>
              </Box>
              <Chip label={item.sourceLabel} size="small" sx={{ bgcolor: brandHome.color.chipFill, color: brandHome.color.bodyAlt, fontWeight: 600, fontSize: '11.5px' }} />
              {item.actionLabel && (
                item.actionHref ? (
                  <Button size="small" variant="outlined" component={RouterLink} to={item.actionHref} sx={{ borderColor: brandHome.color.border, color: brandHome.color.ink }}>
                    {item.actionLabel}
                  </Button>
                ) : (
                  <Button size="small" variant="outlined" sx={{ borderColor: brandHome.color.border, color: brandHome.color.ink }}>
                    {item.actionLabel}
                  </Button>
                )
              )}
            </Box>
          ))}
          {truncatedCount > 0 && (
            <Box sx={{ p: '10px 20px' }}>
              <Typography sx={{ fontSize: '12px', color: brandHome.color.muted }}>
                {truncatedCount} more item{truncatedCount === 1 ? '' : 's'} not shown.
              </Typography>
            </Box>
          )}
        </>
      )}
    </SectionCard>
  );
};
